<script lang="ts">
	import ChatInput from '#lib/ChatInput.svelte';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import ImageLightbox from '#lib/ImageLightbox.svelte';
	import { inactivityTimer } from '#lib/inactivity-timer.ts';
	import Markdown from '#lib/Markdown.svelte';
	import MessageAssets from '#lib/MessageAssets.svelte';
	import ScrollGradient from '#lib/ScrollGradient.svelte';
	import { ChatAutoScroll } from '#lib/util/chat-auto-scroll.svelte.ts';
	import { globalState } from '#lib/util/global-state.svelte.ts';
	import { resolveAbsoluteLink } from '#lib/util/resolve-links.ts';
	import { ANTONIO_AVATAR_SRC_BY_NAME } from '#src/constants/antonio-avatars.ts';
	import { getAntonioChatContext } from '#src/contexts/antonio-chat.context.svelte.ts';
	import { pbCoreApiService } from '#src/services/pb-core-api.service.svelte.ts';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import LoadingIndicator from '@astlai/common/LoadingIndicator.svelte';
	import type { ReferenceImageRecord } from '@astlai/common/types/reference-image';
	import type { InstanceFeatureFlags } from '@astlai/common/types/instance';
	import { untrack } from 'svelte';
	import AntonioChatControls from './AntonioChatControls.svelte';
	import AntonioPick from './AntonioPick.svelte';

	// 3 minutes of inactivity before an auto-reset, matching old Antonio's
	// INACTIVITY_TIMEOUT_MS.
	const INACTIVITY_TIMEOUT_MS = 3 * 60 * 1000;

	interface Props {
		welcomeMessage?: string;
		antonioChatFlags?: InstanceFeatureFlags['antonioChat'];
		chatPlaceholder?: string;
	}

	let {
		welcomeMessage = globalState.instance?.welcome_message,
		antonioChatFlags = globalState.instance?.feature_flags?.antonioChat,
		chatPlaceholder = t('chat.antonioPlaceholder')
	}: Props = $props();

	// Relative to PUBLIC_APP_URL, not the current page: this component also renders inside the
	// embeddable widget on third-party host pages, where a plain "/..." path would resolve against
	// the host's origin instead of ours.
	const antonioAvatarSrc = $derived(
		resolveAbsoluteLink(ANTONIO_AVATAR_SRC_BY_NAME[antonioChatFlags?.avatar ?? 'toni'])
	);

	const assistantName = $derived(antonioChatFlags?.assistantName || 'Antonio');

	const chatCtx = getAntonioChatContext();

	const chatScroll = new ChatAutoScroll({
		getMessages: () => chatCtx.presentableMessages,
		isLatestInProgress: () => chatCtx.latestAssistantMessage?.status === 'in_progress'
	});

	const showOnboarding = $derived(!!antonioChatFlags?.showOnboardingScreen && !chatCtx.started);

	$effect(() => {
		// Without an onboarding screen the funnel opens as soon as the instance is
		// resolved (the widget fetches it first), and again after a reset;
		// otherwise the onboarding screen's button opens it.
		if (
			globalState.instance?.id &&
			!antonioChatFlags?.showOnboardingScreen &&
			chatCtx.step.kind === 'start'
		) {
			untrack(() => chatCtx.start());
		}
	});

	let userInput = $state('');

	/** Step kinds with controls of their own; any other kind (text, start, or one this build doesn't know yet) gets the chat input. */
	const CONTROL_STEP_KINDS = new Set([
		'choice',
		'confirm-generate',
		'image',
		'contact',
		'confirm-publish',
		'end'
	]);

	/**
	 * Whether the footer shows the step's controls instead of the chat input.
	 * The step is whatever the server named last; the buttons and forms in this
	 * flow are fixed UI, not something the LLM composes.
	 */
	const showStepControls = $derived(CONTROL_STEP_KINDS.has(chatCtx.step.kind));

	async function sendMessage() {
		const value = userInput;
		userInput = '';
		await chatCtx.chat(value);
	}

	// Undefined in the embeddable widget, which has no modal outlet.
	const modalCtx = getModalContext();
	let referenceImageModalId: string | undefined;

	// Every photo of every reference image of the instance, expanded by the
	// instance loaders (admins and the public chat only: reference_image is
	// admin-only).
	const referenceImageUrls = $derived(
		(globalState.instance?.expand?.reference_images ?? []).flatMap(
			(referenceImage: ReferenceImageRecord) =>
				referenceImage.images.map((image) => pbCoreApiService.files.getURL(referenceImage, image))
		)
	);

	/**
	 * Ctrl+Alt+R (Cmd+Option+R on a Mac) shows the instance's reference photos,
	 * for whoever runs the screen at an event; pressed again, it hides them.
	 * Matched by physical key, since Option+R types "®" on a Mac.
	 */
	function toggleReferenceImage(event: KeyboardEvent) {
		if (!(event.ctrlKey || event.metaKey) || !event.altKey || event.code !== 'KeyR') {
			return;
		}
		if (!modalCtx || !referenceImageUrls.length) {
			return;
		}
		event.preventDefault();
		const topModal = modalCtx.modalHistory[0];
		if (topModal && topModal.id === referenceImageModalId) {
			modalCtx.closeModal();
			return;
		}
		referenceImageModalId = modalCtx.openModal(
			ImageLightbox,
			{ src: referenceImageUrls },
			{ appearance: 'fullscreen' }
		);
	}
</script>

<svelte:window onkeydown={toggleReferenceImage} />

<div
	class="antonio-chat"
	{@attach inactivityTimer(
		INACTIVITY_TIMEOUT_MS,
		() => chatCtx.reset(),
		!!antonioChatFlags?.autoReset && !showOnboarding
	)}
>
	{#if showOnboarding}
		<div class="initial-screen">
			<div class="initial-message">
				<img
					class="antonio-avatar"
					src={antonioAvatarSrc}
					alt={t('antonio.avatarAlt', { name: assistantName })}
				/>
				{#if welcomeMessage}
					<Markdown source={welcomeMessage} />
				{/if}
			</div>
			<button class="accent start-button" onclick={() => chatCtx.start()}>
				{t('antonio.onboarding.start')}
			</button>
		</div>
	{:else}
		<div class="header">
			<div class="header-row">
				<h2>{t('antonio.chatHeader', { name: assistantName })}</h2>
			</div>
			<ScrollGradient align="bottom" colorVar="--surface-2" />
		</div>
		<div {@attach chatScroll.attach()} class="messages-container">
			<div class="messages-list">
				{#each chatCtx.presentableMessages as message, i (message.id)}
					<div class="message {message.role}">
						{#if message.role === 'assistant'}
							<img
								class="antonio-avatar"
								src={antonioAvatarSrc}
								alt={t('antonio.avatarAlt', { name: assistantName })}
							/>
						{/if}
						{#if message.pick?.length}
							<AntonioPick options={message.pick} />
						{:else}
							<div class="message-body">
								<MessageAssets assets={message.assets} />

								{#if message.content}
									<div class="message-content">
										<Markdown source={message.content} />
									</div>
								{/if}
							</div>
						{/if}
					</div>
				{/each}

				{#if chatCtx.fetchable.fetching && chatCtx.latestAssistantMessage?.status !== 'in_progress' && chatCtx.latestAssistantMessage?.status !== 'completed'}
					<div class="message loading">
						<img
							class="antonio-avatar"
							src={antonioAvatarSrc}
							alt={t('antonio.avatarAlt', { name: assistantName })}
						/>
						{#if chatCtx.generatingImage}
							<span class="generating-hint">{t('antonio.generatingImage')}</span>
						{:else}
							<LoadingIndicator />
						{/if}
					</div>
				{/if}

				{#if chatCtx.fetchable.error}
					<div class="error-message">
						{chatCtx.fetchable.error instanceof Error
							? chatCtx.fetchable.error.message
							: 'Etwas ist schiefgelaufen.'}
						{#if chatCtx.fetchable.error?.cause?.code !== 'usage_limit_exceeded'}
							<button class="glass" onclick={() => chatCtx.retry()}
								>{t('antonio.chat.retryButton')}</button
							>
						{/if}
					</div>
				{/if}

				<div class="spacer" aria-hidden="true" style:height={chatScroll.spacerHeight}></div>
			</div>
		</div>

		<div class="footer">
			<!-- Nothing to answer until the turn ends: the step it ends in is only known then. -->
			{#if chatCtx.isStreaming}
				<!-- Show nothing for now while streaming-->
			{:else if showStepControls}
				<AntonioChatControls
					step={chatCtx.step}
					onInput={(input, displayText) => chatCtx.send(input, displayText)}
					onUploadImage={(file) => chatCtx.uploadImage(file)}
					onRestart={() => chatCtx.reset()}
				/>
			{:else}
				<ChatInput
					bind:value={userInput}
					onchange={sendMessage}
					disabled={chatCtx.fetchable.fetching}
					showSettings={false}
					placeholder={chatPlaceholder}
				/>
			{/if}
		</div>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.antonio-chat {
		flex: 1 1 auto;
		overflow: hidden;
		@include lib.flex-column();
		@include lib.flex-align(flex-start, center);
		background: var(--surface-2);

		.initial-screen {
			flex: 1 1 auto;
			width: lib.$bp-sm;
			max-width: 100%;
			@include lib.flex-column();
			@include lib.flex-align(space-evenly);
			padding: 1rem 0;

			.initial-message {
				@include lib.flex-column();
				@include lib.flex-align();
				font-size: 1rem;
				padding: 0 3rem;
				text-align: center;

				@include lib.bp(gt-xs) {
					font-size: 1.5rem;
				}

				@include lib.bp(gt-sm) {
					font-size: var(--antonio-max-text-size, 2rem);
				}

				.antonio-avatar {
					width: calc(var(--antonio-avatar-size, 200px) * 0.5);
					border-radius: 50%;

					@include lib.bp(gt-xs) {
						width: calc(var(--antonio-avatar-size, 200px) * 0.75);
					}

					@include lib.bp(gt-sm) {
						width: var(--antonio-avatar-size, 200px);
					}
				}
			}

			.start-button {
				font-size: 2.4rem;
				padding: 0;
				border-radius: 50%;
				font-weight: 600;
				width: 180px;
				height: 180px;
				display: flex;
				align-items: center;
				justify-content: center;
			}
		}

		.header {
			flex: 0 0 auto;
			z-index: 2;
			width: 100%;
			position: relative;
			background: var(--surface-2);
			@include lib.flex-column();

			.header-row {
				padding: 1rem;
				@include lib.flex-row();
				@include lib.flex-align(space-between, center);

				h2 {
					flex: 1 1 auto;
					font-size: 1rem;
				}

				button {
					margin: -0.5rem 0;
				}
			}
		}

		.messages-container {
			flex: 1 1 auto;
			overflow-y: auto;
			width: 100%;
			@include lib.flex-column();
			@include lib.flex-align(flex-start, center);

			.messages-list {
				position: relative;
				width: lib.$bp-sm;
				max-width: 100%;
				@include lib.flex-column();
				padding: 0.5rem 1rem 1rem;
				gap: 2rem;

				@include lib.bp(gt-sm) {
					padding: 1rem;
				}

				.message {
					font-size: 0.9rem;
					line-height: 1.3rem;
					@include lib.flex-row();
					gap: 1rem;

					@include lib.bp(gt-sm) {
						font-size: 1.2rem;
						line-height: 1.7rem;
						gap: 1.5rem;
					}

					.antonio-avatar {
						flex: 0 0 auto;
						width: 32px;
						height: 32px;
						border-radius: 50%;
						margin: 0.2em 0;
						background: var(--surface-3);

						@include lib.bp(gt-sm) {
							width: 56px;
							height: 56px;
						}
					}

					.message-body {
						flex: 1 1 auto;
						@include lib.flex-column();
					}

					.message-content {
						padding: 0.25rem 0;

						@include lib.bp(gt-sm) {
							padding: 0.5rem 0;
						}
					}

					&.user {
						max-width: 80%;
						align-self: flex-end;
						.message-content {
							border-radius: 1rem;
							padding: 0.5rem 1rem;
							background-color: var(--surface-3);
							color: var(--text-primary);
						}
					}

					&.assistant {
						align-self: stretch;
					}

					&.loading {
						@include lib.flex-align(flex-start, center);
						color: var(--text-secondary);
					}
				}

				.error-message {
					color: var(--text-error);
					background-color: var(--surface-error);
					padding: 1rem;
					border-radius: 1rem;
					margin: 0.5rem 0;
					@include lib.flex-row();
					@include lib.flex-align(space-between, center);
				}

				.spacer {
					flex: 0 0 auto;
					// background: red; // for debugging
				}
			}
		}

		.footer {
			max-width: lib.$bp-sm;
			width: 100%;
			padding: 0.5rem;
		}

		.footer-loading {
			@include lib.flex-align(center, center);
			min-height: 3rem;
		}
	}
</style>
